import { dec, isDecimal, max, min, round, toFixed, ZERO } from './decimal';

/** Tender and change maths for counter sales (shared with the POS module). */

export interface TenderResult {
  /** Money applied to the sale. */
  applied: string;
  /** Change to hand back. */
  change: string;
  /** Left on the customer's account (credit sale). */
  remaining: string;
}

/** Tender math: anything above the total is change; anything short stays on account. */
export function tender(total: string, tendered: string, decimals: number): TenderResult {
  const t = dec(total);
  const given = isDecimal(tendered) ? max(dec(tendered), ZERO) : ZERO;
  const applied = min(given, t);
  return {
    applied: toFixed(applied, decimals),
    change: toFixed(max(given - t, ZERO), decimals),
    remaining: toFixed(max(t - given, ZERO), decimals),
  };
}

/**
 * Suggested cash amounts: the exact total, then the next round notes above it (100, 500, 1 000, 5 000)
 * — at most four distinct values, ascending.
 */
export function quickCash(total: string, decimals: number): string[] {
  const t = dec(total);
  if (t <= 0n) return [];
  const out = [toFixed(t, decimals)];
  for (const note of ['100', '500', '1000', '5000']) {
    const n = dec(note);
    const up = ((t + n - 1n) / n) * n;
    const v = toFixed(round(up, 0), decimals);
    if (!out.includes(v)) out.push(v);
    if (out.length === 4) break;
  }
  return out;
}

/** The part of a customer the credit check needs (a contact from the API fits). */
export interface CreditCustomer {
  isWalkIn: boolean;
  creditLimit: string | null;
  balances: { receivable: { signed: string } };
}

/** Customer's account before and after this sale: due now, credit left, and whether it goes over the limit. */
export interface CreditState {
  /** Owed now (negative = the customer is in credit). */
  due: string;
  /** Owed after this sale. */
  after: string;
  limit: string | null;
  /** Limit minus what is owed now; null without a limit. */
  left: string | null;
  over: boolean;
}

export function creditCheck(
  customer: CreditCustomer | null,
  remaining: string,
  decimals: number,
): CreditState | null {
  // No balances yet (a document's contact, before the account loads): nothing to check.
  if (!customer || customer.isWalkIn || !(customer.balances as unknown)) return null;
  const due = dec(customer.balances.receivable.signed);
  const adding = isDecimal(remaining) ? max(dec(remaining), ZERO) : ZERO;
  const after = due + adding;
  const limit = customer.creditLimit;
  return {
    due: toFixed(due, decimals),
    after: toFixed(after, decimals),
    limit,
    left: limit === null ? null : toFixed(dec(limit) - max(due, ZERO), decimals),
    over: limit !== null && adding > 0n && after > dec(limit),
  };
}
